import gql from 'graphql-tag';
import apolloClient from '../apollo';
import dayjs from 'dayjs';

import relativeTime from 'dayjs/plugin/relativeTime';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekday from 'dayjs/plugin/weekday';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import 'dayjs/locale/de';
import Vue from 'vue';

dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(dayOfYear);
dayjs.locale('de');

/**
 * State
 */
const defaultAppointment = () => {
  return {
    id: '_new',
    number: '_new',
    appointmentType: 'AC',
    appointmentState: 'SCHEDULED',
    appointmentApprovedState: 'NO',
    installationStartAt: dayjs()
      .startOf('day')
      .add(8, 'hour')
      .toDate(),
    installationEndAt: dayjs()
      .startOf('day')
      .add(16, 'hour')
      .toDate(),
    project: null,
    employees: [],
    backlog: null,
    itemStorages: [],
    isOutdated: null,
    markedAsChanged: null,
  };
};

const getDefaultState = () => {
  return {
    appointments: [],
    appointmentCount: 0,
  };
};
const state = getDefaultState();

/**
 * Defines the fields which are requested for an appointment
 * used for all queries/mutations which return an appointment
 * @type {DocumentNode}
 */
const APPOINTMENT_REQUEST_FIELDS = gql`
  fragment appointmentFields on Appointment {
    id
    number
    project {
      id
      number
      employerProjectNumber
      substructureVerificationState
      dcVerificationState
      acVerificationState
      measurementVerificationState
      measurementApprovementVerificationState
      serviceVerificationState
      serviceApprovementVerificationState
      serviceApprovementNote
      acceptanceApprovementVerificationState
      acceptanceApprovementNote
      invoiceApprovementVerificationState
      invoiceApprovementNote
      technicalReview {
        installationTeamNotes
        dcNotes
        dcInstallationTeamNotes
        acNotes
      }
      customer {
        firstname
        lastname
        street
        streetNumber
        streetNumberSuffix
        zip
        city
        email
        phone
        mobile
      }
      solarPlant {
        solarPanelCount
      }
      employer {
        id
        number
        name
      }
      powerCompany {
        number
        _id
        company
      }
    }
    employees {
      _id
      number
      firstname
      lastname
    }
    itemStorages {
      _id
      number
      name
      itemStorageType
    }
    installationStartAt
    installationEndAt
    appointmentType
    appointmentState
    appointmentApprovedState
    description
    deletionPolicy {
      canDelete
      reason
    }
    backlog {
      id
      backlogReason
      description
      remainingWork
      remainingTimeExpenditure
      createdAt
      updatedAt
    }
  }
`;

/**
 * Payload of the block project-installation-appointments (`ProjectAppointmentInput`): without
 * project, the server sets it.
 * @param {*} appointment
 */
const createProjectAppointmentPayload = (appointment) => {
  // console.log(appointment);

  /** Only send ID's for employees */
  const employeeIds = [];
  if (appointment.employees) {
    appointment.employees.map((employee) => {
      employeeIds.push(employee._id);
    });
  }

  /** Only send ID's for itemStorages */
  const itemStorageIds = [];
  if (appointment.itemStorages) {
    appointment.itemStorages.map((employee) => {
      itemStorageIds.push(employee._id);
    });
  }

  if (appointment.backlog) {
    delete appointment.backlog.__typename;
    delete appointment.backlog.id;
    delete appointment.backlog.createdAt;
    delete appointment.backlog.updatedAt;
  }

  const payload = {
    ...(appointment.installationStartAt
      ? { installationStartAt: appointment.installationStartAt }
      : {}),
    ...(appointment.installationEndAt ? { installationEndAt: appointment.installationEndAt } : {}),
    ...(appointment.appointmentType ? { appointmentType: appointment.appointmentType } : {}),
    ...(appointment.appointmentState ? { appointmentState: appointment.appointmentState } : {}),
    ...(appointment.appointmentApprovedState
      ? { appointmentApprovedState: appointment.appointmentApprovedState }
      : {}),
    employees: employeeIds,
    itemStorages: itemStorageIds,
    description: appointment.description,
    backlog: appointment.backlog,
  };
  return payload;
};

/**
 * sanitize inputs and create payload to send to the graphql api
 * @param {*} appointment
 */
const createPayload = (appointment) => ({
  ...createProjectAppointmentPayload(appointment),
  project: appointment.project ? appointment.project.id : null,
});

const APPOINTMENT_LIST_QUERY = gql`
  query appointmentPages(
    $page: Int!
    $pageSize: Int!
    $sortField: String
    $sortOrder: Int
    $filters: AppointmentFilterInput
  ) {
    appointmentPages(
      page: $page
      pageSize: $pageSize
      sortField: $sortField
      sortOrder: $sortOrder
      filters: $filters
    ) {
      appointments {
        ...appointmentFields
      }
      appointmentCount
    }
  }
  ${APPOINTMENT_REQUEST_FIELDS}
`;

/** Appointment list of the block project-installation-appointments; the server restricts it to the project. */
const PROJECT_APPOINTMENT_LIST_QUERY = gql`
  query projectAppointments(
    $projectNumber: Int!
    $page: Int!
    $pageSize: Int!
    $sortField: String
    $sortOrder: Int
    $filters: AppointmentFilterInput
  ) {
    projectByNumber(projectNumber: $projectNumber) {
      id
      number
      blocks {
        installationAppointments {
          id
          number
          appointments(
            page: $page
            pageSize: $pageSize
            sortField: $sortField
            sortOrder: $sortOrder
            filters: $filters
          ) {
            appointments {
              ...appointmentFields
            }
            appointmentCount
          }
        }
      }
    }
  }
  ${APPOINTMENT_REQUEST_FIELDS}
`;

/**
 * Request of the appointment list: with a project number the block
 * project-installation-appointments, without it the global query appointmentPages. `pagesOf`
 * returns the `AppointmentPages` of the response.
 */
const appointmentListRequest = (projectNumber) => {
  if (projectNumber) {
    return {
      query: PROJECT_APPOINTMENT_LIST_QUERY,
      pagesOf: (data) => data.projectByNumber.blocks.installationAppointments.appointments,
    };
  }
  return { query: APPOINTMENT_LIST_QUERY, pagesOf: (data) => data.appointmentPages };
};

/**
 * The block passes `{ appointment, projectNumber }`, the other callers the appointment itself;
 * the path is chosen by the explicit project number, never by `appointment.project`.
 */
const appointmentAndProjectNumberOf = (payload) =>
  payload.projectNumber ? payload : { appointment: payload, projectNumber: null };

/**
 * Request of a create: with a project number the mutation of the block
 * project-installation-appointments (payload without project), without it the global mutation.
 */
const createAppointmentRequest = (appointment, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation createProjectAppointment(
          $projectNumber: Int!
          $payload: ProjectAppointmentInput!
        ) {
          createProjectAppointment(projectNumber: $projectNumber, appointmentInput: $payload) {
            ...appointmentFields
          }
        }
        ${APPOINTMENT_REQUEST_FIELDS}
      `,
      variables: { projectNumber, payload: createProjectAppointmentPayload(appointment) },
      resultField: 'createProjectAppointment',
    };
  }
  return {
    mutation: gql`
      mutation createAppointment($payload: AppointmentInput!) {
        createAppointment(appointmentInput: $payload) {
          ...appointmentFields
        }
      }
      ${APPOINTMENT_REQUEST_FIELDS}
    `,
    variables: { payload: createPayload(appointment) },
    resultField: 'createAppointment',
  };
};

/** Request of an update, chosen by the project number as `createAppointmentRequest`. */
const updateAppointmentRequest = (appointment, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation updateProjectAppointment(
          $projectNumber: Int!
          $appointmentNumber: Int!
          $payload: ProjectAppointmentInput!
        ) {
          updateProjectAppointment(
            projectNumber: $projectNumber
            appointmentNumber: $appointmentNumber
            appointmentInput: $payload
          ) {
            ...appointmentFields
          }
        }
        ${APPOINTMENT_REQUEST_FIELDS}
      `,
      variables: {
        projectNumber,
        appointmentNumber: appointment.number,
        payload: createProjectAppointmentPayload(appointment),
      },
      resultField: 'updateProjectAppointment',
    };
  }
  return {
    mutation: gql`
      mutation updateAppointment($appointmentNumber: Int!, $payload: AppointmentInput!) {
        updateAppointment(appointmentNumber: $appointmentNumber, appointmentInput: $payload) {
          ...appointmentFields
        }
      }
      ${APPOINTMENT_REQUEST_FIELDS}
    `,
    variables: {
      appointmentNumber: appointment.number,
      payload: createPayload(appointment),
    },
    resultField: 'updateAppointment',
  };
};

/** Request of a delete, chosen by the project number as `createAppointmentRequest`. */
const deleteAppointmentRequest = (appointmentNumber, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation deleteProjectAppointment($projectNumber: Int!, $appointmentNumber: Int!) {
          deleteProjectAppointment(
            projectNumber: $projectNumber
            appointmentNumber: $appointmentNumber
          )
        }
      `,
      variables: { projectNumber, appointmentNumber },
    };
  }
  return {
    mutation: gql`
      mutation deleteAppointment($appointmentNumber: Int!) {
        deleteAppointment(appointmentNumber: $appointmentNumber)
      }
    `,
    variables: { appointmentNumber },
  };
};

/** Request of the mail, chosen by the project number as `createAppointmentRequest`. */
const sendAppointmentMailRequest = (appointmentNumber, projectNumber) => {
  if (projectNumber) {
    return {
      mutation: gql`
        mutation sendProjectAppointmentMail($projectNumber: Int!, $appointmentNumber: Int!) {
          sendProjectAppointmentMail(
            projectNumber: $projectNumber
            appointmentNumber: $appointmentNumber
          ) {
            ...appointmentFields
          }
        }
        ${APPOINTMENT_REQUEST_FIELDS}
      `,
      variables: { projectNumber, appointmentNumber },
      resultField: 'sendProjectAppointmentMail',
    };
  }
  return {
    mutation: gql`
      mutation sendAppointmentMail($appointmentNumber: Int!) {
        sendAppointmentMail(appointmentNumber: $appointmentNumber) {
          ...appointmentFields
        }
      }
      ${APPOINTMENT_REQUEST_FIELDS}
    `,
    variables: { appointmentNumber },
    resultField: 'sendAppointmentMail',
  };
};

/**
 * Actions
 */
const actions = {
  /**
   * Loads a page of appointments. With `projectNumber` the appointments of the block
   * project-installation-appointments, without it the global list.
   */
  async fetchAppointmentPages(
    { commit, dispatch },
    { page, pageSize, sortField, sortOrder, filters, projectNumber }
  ) {
    const { query, pagesOf } = appointmentListRequest(projectNumber);
    try {
      const response = await apolloClient.query({
        query,
        variables: {
          ...(projectNumber ? { projectNumber } : {}),
          page: page,
          pageSize: pageSize,
          sortField: sortField,
          sortOrder: sortOrder,
          filters: filters,
        },
        fetchPolicy: 'network-only',
      });
      const appointmentPages = pagesOf(response.data);
      commit('SET_APPOINTMENTS', appointmentPages.appointments);
      commit('SET_APPOINTMENT_COUNT', appointmentPages.appointmentCount);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchAppointments({ commit, dispatch }, { filters }) {
    try {
      const response = await apolloClient.query({
        query: gql`
          query appointments($filters: AppointmentFilterInput) {
            appointments(filters: $filters) {
              ...appointmentFields
            }
          }
          ${APPOINTMENT_REQUEST_FIELDS}
        `,
        variables: {
          filters: filters,
        },
      });
      commit('SET_APPOINTMENTS', response.data.appointments);
    } catch (err) {
      throw new Error(err);
    }
  },

  async fetchAppointmentByNumber({ commit, dispatch }, appointmentNumber) {
    // console.log('fetchAppointmentByNumber()', appointmentNumber);
    try {
      const { data, errors } = await apolloClient.query({
        query: gql`
          query appointmentByNumber($appointmentNumber: Int!) {
            appointmentByNumber(appointmentNumber: $appointmentNumber) {
              ...appointmentFields
            }
          }
          ${APPOINTMENT_REQUEST_FIELDS}
        `,
        variables: {
          appointmentNumber: parseInt(appointmentNumber),
        },
      });

      if (errors) {
        // dispatch('handleErrors', errors);
        return errors;
      }

      commit('ADD_OR_UPDATE_APPOINTMENT', data.appointmentByNumber);
    } catch (err) {
      // dispatch('handleErrors', errors);
      throw new Error(err);
    }
  },

  /**
   * Creates the appointment; with `{ appointment, projectNumber }` through the block
   * project-installation-appointments.
   */
  async createAppointment({ commit, dispatch }, appointmentOrBlockPayload) {
    const { appointment, projectNumber } = appointmentAndProjectNumberOf(appointmentOrBlockPayload);
    const { mutation, variables, resultField } = createAppointmentRequest(
      appointment,
      projectNumber
    );

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      const createdAppointment = response.data[resultField];
      dispatch('message', {
        message: 'Termin gespeichert!',
        type: 'success',
        lifetime: 2,
      });
      commit('REMOVE_APPOINTMENT', '_new'); // remove temporary _new appointment
      commit('ADD_OR_UPDATE_APPOINTMENT', createdAppointment);
      return createdAppointment.number;
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim erstellen! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Updates the appointment; with `{ appointment, projectNumber }` through the block
   * project-installation-appointments.
   */
  async updateAppointment({ commit, dispatch }, appointmentOrBlockPayload) {
    const { appointment, projectNumber } = appointmentAndProjectNumberOf(appointmentOrBlockPayload);
    const { mutation, variables, resultField } = updateAppointmentRequest(
      appointment,
      projectNumber
    );

    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Termin aktualisiert!',
        type: 'success',
        lifetime: 2,
      });
      commit('ADD_OR_UPDATE_APPOINTMENT', response.data[resultField]);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim bearbeiten! Bitte Eingabefelder überprüfen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * Deletes the appointment; with `{ appointmentNumber, projectNumber }` through the block
   * project-installation-appointments, the other callers pass the appointment number.
   */
  async deleteAppointment({ commit, dispatch }, appointmentNumberOrBlockPayload) {
    const { appointmentNumber, projectNumber } =
      typeof appointmentNumberOrBlockPayload === 'object'
        ? appointmentNumberOrBlockPayload
        : { appointmentNumber: appointmentNumberOrBlockPayload, projectNumber: null };
    try {
      const response = await apolloClient.mutate(
        deleteAppointmentRequest(appointmentNumber, projectNumber)
      );
      dispatch('message', {
        message: 'Termin gelöscht!',
        type: 'success',
        lifetime: 2,
      });
      // console.log(response.data.deleteAppointment);

      commit('REMOVE_APPOINTMENT', appointmentNumber);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim löschen!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /** Sends the appointment mail; with `projectNumber` through the block project-installation-appointments. */
  async sendAppointmentMail({ commit, dispatch }, { appointmentNumber, projectNumber }) {
    const { mutation, variables, resultField } = sendAppointmentMailRequest(
      appointmentNumber,
      projectNumber
    );
    try {
      const response = await apolloClient.mutate({ mutation, variables });
      dispatch('message', {
        message: 'Nachricht verschickt!',
        type: 'success',
        lifetime: 1,
      });

      commit('ADD_OR_UPDATE_APPOINTMENT', response.data[resultField]);
    } catch (err) {
      dispatch('message', {
        message: 'Fehler beim Versand!',
        type: 'danger',
        lifetime: 1,
      });
      throw new Error(err);
    }
  },

  /**
   * create a new appointment object in store and return it
   * @param {*} param0
   * @param {*} appointment
   */
  async initAppointment({ commit }) {
    commit('ADD_OR_UPDATE_APPOINTMENT', defaultAppointment());
  },

  /**
   * create a new appointment with existing data
   * @param {*} param0
   * @param {*} appointment
   */
  async duplicateAppointment({ commit }, { appointment, installationStartAt }) {
    const duplicate = Object.assign(defaultAppointment(), {
      appointmentType: appointment.appointmentType,
      project: appointment.project,
      employees: appointment.employees,
      description: appointment.description,
      installationStartAt: installationStartAt,
      installationEndAt: dayjs(installationStartAt)
        .add(8, 'hour')
        .toDate(),
    });

    // console.log(duplicate);
    commit('ADD_OR_UPDATE_APPOINTMENT', duplicate);
    return duplicate;
  },

  appointmentLocalUpdate({ commit }, appointment) {
    commit('ADD_OR_UPDATE_APPOINTMENT', appointment);
  },

  appointmentCleanup({ commit }) {
    commit('REMOVE_APPOINTMENT', '_new');
  },
};

/**
 * Mutations
 */
const mutations = {
  SET_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_APPOINTMENT_DEFAULT_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
  SET_APPOINTMENTS: (state, appointments) =>
    appointments ? (state.appointments = appointments) : (state.appointments = []),
  ADD_APPOINTMENT: (state, appointment) => state.appointments.push(appointment),
  ADD_OR_UPDATE_APPOINTMENT: (state, updatedAppointment) => {
    const existingAppointment = state.appointments.find(
      (appointment) => appointment.id === updatedAppointment.id
    );
    if (existingAppointment) {
      Object.assign(existingAppointment, updatedAppointment);
    } else {
      state.appointments.push(updatedAppointment);
    }
  },
  REMOVE_APPOINTMENT: (state, appointmentNumber) => {
    // console.log(appointmentNumber);
    const index = state.appointments.findIndex(
      (appointment) => appointment.number === appointmentNumber
    );
    state.appointments.splice(index, 1);
  },
  SET_APPOINTMENT_COUNT: (state, appointmentCount) => (state.appointmentCount = appointmentCount),
};

/**
 * Getters
 */
const getters = {
  getAppointments: (state) => {
    /**
     * appointments are grouped by weekday in the table view, map each installationStartAt date to the weekday
     */
    state.appointments.map((appointment) => {
      appointment.weekday = dayjs(appointment.installationStartAt).format('dddd DD.MM.YYYY');
      appointment.installationStartAt = dayjs(appointment.installationStartAt).toDate();
      appointment.installationEndAt = dayjs(appointment.installationEndAt).toDate();
      appointment.title =
        appointment.project && appointment.project.customer
          ? appointment.project.customer.firstname + ' ' + appointment.project.customer.lastname
          : '';
      appointment.start = appointment.installationStartAt;
      appointment.end = appointment.installationEndAt;
      Vue.set(appointment, 'isOutdated', false);
      Vue.set(appointment, 'markedAsChanged', false);
    });

    return state.appointments;
  },
  getAppointment: (state) => (appointmentId) =>
    state.appointments.find((appointment) => appointment.id == appointmentId),
  getAppointmentByNumber: (state) => (appointmentNumber) =>
    state.appointments.find((appointment) => appointment.number == appointmentNumber),
  getAppointmentCount: (state) => state.appointmentCount,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
